/**
 * Common constant for displaying not available/empty values consistently across the app
 * Used instead of "N/A" for a cleaner look
 */
export const NOT_AVAILABLE = '--';

/**
 * Formats date strings/objects consistently
 * @param dateString - Date string, Date object, or null/undefined
 * @param short - If true, omits the year from the output (e.g., "Jan 15" instead of "Jan 15, 2025")
 */
export const formatDate = (dateString: string | Date | null | undefined, short = false) => {
  if (!dateString) return NOT_AVAILABLE;
  return new Date(dateString).toLocaleDateString('en-US', {
    ...(!short && { year: 'numeric' }),
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats date with time (mm/dd/yy HH:MM format)
 */
export const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return NOT_AVAILABLE;
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}/${day}/${year} ${hours}:${minutes}`;
};

/**
 * Formats file size in human-readable format
 * Examples:
 * - 1024 -> "1.0 KB"
 * - 1048576 -> "1.0 MB"
 * - 1073741824 -> "1.0 GB"
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const decimals = 1;

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  const unit = units.at(i) ?? 'B';

  return `${size.toFixed(decimals)} ${unit}`;
};

const LOGFIRE_BASE_URL = import.meta.env.VITE_LOGFIRE_BASE_URL as string || 'https://logfire-eu.pydantic.dev';
const LOGFIRE_ORG = import.meta.env.VITE_LOGFIRE_ORG as string || 'harmony';

export const LOGFIRE_PROJECTS = {
  v2: import.meta.env.VITE_LOGFIRE_PROJECT_V2 as string || 'agents-api',
  v1: import.meta.env.VITE_LOGFIRE_PROJECT_V1 as string || 'agent-workflows-orchestrator',
} as const;

/**
 * Detect the Logfire project key based on the raw conversation's agent_version field.
 * V2 conversations have agent_version='v2', everything else is v1.
 */
export const detectLogfireProject = (agentVersion?: string): keyof typeof LOGFIRE_PROJECTS => {
  return agentVersion === 'v2' ? 'v2' : 'v1';
};

/**
 * Build a Logfire URL to view a trace, optionally with a specific span.
 * URL format: /{org}/{project}?q=trace_id='...'&traceId=...&since=...&until=...
 */
export const buildLogfireTraceUrl = (
  traceId: string,
  project: keyof typeof LOGFIRE_PROJECTS = 'v2',
  options?: { spanId?: string; since?: string; until?: string },
): string => {
  // eslint-disable-next-line security/detect-object-injection
  const projectSlug = LOGFIRE_PROJECTS[project];
  const params = new URLSearchParams();

  // Build the query filter
  let query = `trace_id='${traceId}'`;
  if (options?.spanId) {
    query += ` and span_id='${options.spanId}'`;
    params.set('spanId', options.spanId);
  }
  params.set('q', query);
  params.set('traceId', traceId);

  params.set('env', '-clear-');
  if (options?.since) params.set('since', options.since);
  if (options?.until) params.set('until', options.until);

  return `${LOGFIRE_BASE_URL}/${LOGFIRE_ORG}/${projectSlug}?${params.toString()}`;
};

const GRAFANA_BASE_URL = import.meta.env.VITE_GRAFANA_BASE_URL as string || 'https://harmonyso.grafana.net';
const GRAFANA_BUFFER_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Extract the thread ID from a source_identifier string.
 * Expected format: "thread:<uuid>"
 */
export const extractThreadId = (sourceIdentifier?: string | null): string | null => {
  if (!sourceIdentifier) return null;
  const match = /^thread:(.+)$/.exec(sourceIdentifier);
  return match?.[1] ?? null;
};

/**
 * Build a Grafana Explore Traces URL to view traces filtered by thread ID.
 * Applies a 30-minute buffer before and after the conversation time range.
 */
export const buildGrafanaTraceUrl = (
  threadId: string,
  options?: { since?: string | Date; until?: string | Date },
): string => {
  let from = 'now-24h';
  let to = 'now';

  if (options?.since) {
    const sinceMs = new Date(options.since).getTime();
    if (Number.isFinite(sinceMs)) {
      from = String(sinceMs - GRAFANA_BUFFER_MS);
    }
  }
  if (options?.until) {
    const untilMs = new Date(options.until).getTime();
    if (Number.isFinite(untilMs)) {
      to = String(untilMs + GRAFANA_BUFFER_MS);
    }
  }

  const params = new URLSearchParams();
  params.set('from', from);
  params.set('to', to);
  params.set('timezone', 'browser');
  params.set('var-ds', 'grafanacloud-traces');
  params.set('var-primarySignal', 'true');
  params.set('var-filters', `span.harmony.thread.id|=|${threadId}`);
  params.set('var-metric', 'rate');
  params.set('var-groupBy', 'resource.service.name');
  params.set('var-spanListColumns', '');
  params.set('var-latencyThreshold', '');
  params.set('var-partialLatencyThreshold', '');
  params.set('var-durationPercentiles', '0.9');
  params.set('actionView', 'traceList');

  return `${GRAFANA_BASE_URL}/a/grafana-exploretraces-app/explore?${params.toString()}`;
};
